import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** 10/20: on a co-funded deal, what PFS and DIB each owe the partner who paid the broker (separate payers, 10/19 ruling).
 *  Read-only; mark-paid lives on Syndications → Partner Ledger → Co-funded. Renders nothing on a house deal. */
export default function CofundedOwedCard({ dealId, getAuthHeaders }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!dealId || !getAuthHeaders) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/cofunded-payables/deal/${dealId}`, { headers: getAuthHeaders() });
        setData(res.data);
      } catch { setData(null); }
    })();
  }, [dealId, getAuthHeaders]);
  if (!data?.cofunded) return null;
  const rows = data.rows || [];
  return (
    <Card className="border-indigo-200" data-testid="cofunded-owed-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-indigo-900">Co-funded · owed to {data.partner_name || 'partner'} on the broker commission</CardTitle>
        <p className="text-xs text-slate-500">The partner paid the broker. Each PFS and each DIB position owes its own % of the commission — separate payers.
          Mark paid on the <Link to="/partner-ledger" className="underline">Partner Ledger → Co-funded</Link>.</p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">Neither PFS nor DIB holds a position on this deal — nothing owed from the house side.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {rows.map((r) => (
              <div key={r.key} className="rounded border border-slate-200 p-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{r.payer} <span className="text-slate-500 font-normal">· {r.payer_pct}% of {formatCurrency(r.commission)}</span></div>
                  <div className="text-xs text-slate-500">{r.status === 'paid' ? `paid ${String(r.paid_date || '').slice(0, 10)}${r.reference ? ` · ${r.reference}` : ''}` : 'open'}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold">{formatCurrency(r.amount_owed)}</div>
                  <Badge variant={r.status === 'paid' ? 'default' : 'destructive'}>{r.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
